import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import type {
  BoundedText,
  IssueReason,
  Section,
  WorkloadKindSummary,
  WorkloadsOverviewPodList,
  WorkloadsOverviewPodRow,
} from "@k8s-dashboard/contracts";
import { HttpError } from "@/api/client";
import { useWorkloadsOverview } from "@/api/queries";
import { Panel, StatTile, StatusBadge } from "@/components/primitives";
import { VirtualTable } from "@/components/VirtualTable";
import { refPath } from "@/components/drill";
import { PageError, PageHeader } from "@/features/drill/common";
import { ErrorState } from "@/components/SectionState";
import { num } from "@/lib/format";
import { useDashboardParams } from "@/state/useDashboardParams";
import { useT } from "@/i18n/I18nProvider";
import type { Translate } from "@/i18n/I18nProvider";

const MAX_QUERY_BYTES = 128;

function utf8Bytes(value: string): number {
  return new TextEncoder().encode(value).length;
}

export function WorkloadsOverview() {
  const { t } = useT("workload");
  const { clusterId, namespace } = useDashboardParams();
  const { search } = useLocation();
  const [params, setParams] = useSearchParams();
  const committed = params.get("q") ?? "";
  const [draft, setDraft] = useState(committed);
  const [dirtyIdentity, setDirtyIdentity] = useState<string | null>(null);
  const query = useWorkloadsOverview(clusterId, namespace, committed);
  const dirtyRevision = useRef(0);
  const queryIdentity = useRef(query.requestIdentity);

  if (queryIdentity.current !== query.requestIdentity) {
    queryIdentity.current = query.requestIdentity;
    dirtyRevision.current += 1;
  }

  useEffect(() => setDraft(committed), [committed]);
  useEffect(() => {
    const markDirty = () => {
      dirtyRevision.current += 1;
      setDirtyIdentity(queryIdentity.current);
    };
    window.addEventListener("dashboard-stream-message", markDirty);
    return () => window.removeEventListener("dashboard-stream-message", markDirty);
  }, []);

  const submittedSearch = useMemo(() => {
    const next = new URLSearchParams(search);
    next.delete("q");
    return next.toString();
  }, [search]);
  const tooLong = utf8Bytes(draft.trim()) > (query.data?.limits.maxQueryBytes ?? MAX_QUERY_BYTES);
  const dirty = dirtyIdentity === query.requestIdentity;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (tooLong) return;
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      const value = draft.trim();
      if (value) next.set("q", value);
      else next.delete("q");
      return next;
    }, { replace: true });
  };

  const refresh = () => {
    if (!query.canFetch) return;
    const identity = query.requestIdentity;
    const revision = dirtyRevision.current;
    void query.refetch().then((result) => {
      if (!result.isSuccess || result.isError) return;
      if (queryIdentity.current !== identity || dirtyRevision.current !== revision) return;
      setDirtyIdentity((current) => current === identity ? null : current);
    });
  };

  const centralUnsupported = query.error instanceof HttpError && query.error.body.code === "central_mode_unsupported";

  return (
    <div className="page">
      <PageHeader
        title={t("workload.overview.title")}
        subtitle={`${clusterId} · ${namespace === "all" ? t("workload.overview.allNamespaces") : namespace}`}
        controls={
          <button type="button" className="btn" onClick={refresh} disabled={query.isFetching || !query.canFetch}>
            {query.isFetching ? t("workload.overview.refreshing") : t("workload.overview.refresh")}
          </button>
        }
      />

      {dirty ? (
        <div className="notice notice--warning" role="status">
          {t("workload.overview.dirty")}
        </div>
      ) : null}

      <form className="toolbar row row--wrap" onSubmit={submit}>
        <label>
          {t("workload.overview.search")}
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t("workload.overview.searchPlaceholder")}
            aria-invalid={tooLong}
          />
        </label>
        <button className="btn btn--primary" type="submit" disabled={tooLong}>{t("workload.overview.searchSubmit")}</button>
        <span className="muted">{t("workload.overview.searchHelp", { current: utf8Bytes(draft.trim()), max: query.data?.limits.maxQueryBytes ?? MAX_QUERY_BYTES })}</span>
        {tooLong ? <span role="alert">{t("workload.overview.searchTooLong", { max: MAX_QUERY_BYTES })}</span> : null}
      </form>

      {query.scopeError ? (
        <section className="panel">
          <div className="panel__body">
            <ErrorState
              detail={t("workload.overview.scopeError")}
              onRetry={query.scopeRetrying ? undefined : query.retryScope}
            />
            {query.scopeRetrying ? <p className="muted" role="status">{t("workload.overview.scopeRetrying")}</p> : null}
          </div>
        </section>
      ) : query.authorizationPending ? (
        <section className="panel"><div className="panel__body" role="status">{t("workload.overview.scopePending")}</div></section>
      ) : query.isError ? (
        centralUnsupported ? (
          <section className="panel"><div className="panel__body">{t("workload.overview.centralUnsupported")}</div></section>
        ) : <PageError error={query.error} onRetry={refresh} />
      ) : (
        <>
          <WorkloadSummaryPanel section={query.data?.workloads} loading={query.isLoading} />
          <PodPanel section={query.data?.pods} loading={query.isLoading} search={submittedSearch} />
        </>
      )}

      <p className="muted">
        {t("workload.overview.eventsNote")} <Link to="/events">{t("workload.overview.eventsLink")}</Link>
      </p>
    </div>
  );
}

function WorkloadSummaryPanel({ section, loading }: { section?: Section<WorkloadKindSummary[]>; loading: boolean }) {
  const { t } = useT("workload");
  return (
    <Panel title={t("workload.overview.kindTitle")} subtitle={t("workload.overview.kindSubtitle")} section={section}>
      {section?.data ? (
        <div className="stats-grid">
          {section.data.map((kind) => (
            <StatTile
              key={kind.kind}
              label={kind.kind}
              value={num(kind.total)}
              tone={kind.critical > 0 ? "critical" : kind.warning > 0 ? "warning" : undefined}
              footnote={`${t("workload.overview.kindFootnote", {
                healthy: kind.healthy,
                warning: kind.warning,
                critical: kind.critical,
                restarts: kind.restarts.state === "exact" ? num(kind.restarts.value) : t("workload.overview.unavailable"),
              })}${kind.issues.length ? ` · ${kind.issues.map((issue) => t("workload.overview.issueCount", { issue: issueLabel(issue.reason, t), count: issue.workloads })).join(", ")}` : ""}`}
            />
          ))}
          <p className="muted">{t("workload.overview.cronRestart")}</p>
        </div>
      ) : loading ? <p className="muted">{t("workload.overview.workloadsLoading")}</p> : <DegradedSection reason={section?.reason} />}
    </Panel>
  );
}

function PodPanel({ section, loading, search }: { section?: Section<WorkloadsOverviewPodList>; loading: boolean; search: string }) {
  const { t } = useT("workload");
  const list = section?.data;
  return (
    <Panel title={t("workload.overview.podsTitle")} subtitle={t("workload.overview.podsSubtitle")} section={section} flush>
      {list ? (
        <>
          <p className="muted" style={{ padding: "var(--space-4)", margin: 0 }}>
            {t("workload.overview.podsCoverage", { total: list.total, shown: list.shown })}
            {list.truncated ? ` · ${t("workload.overview.podsTruncated", { omitted: list.omitted })}` : ` · ${t("workload.overview.exact")}`}
          </p>
          {list.total === 0 && list.shown === 0 && !list.truncated && list.coverage === "exact" ? (
            <p className="muted">{t("workload.overview.podsEmpty")}</p>
          ) : list.shown === 0 ? (
            <p className="muted" role="status">{t("workload.overview.podsTrimmedAll", { omitted: list.omitted })}</p>
          ) : <PodTable rows={list.rows} search={search} />}
        </>
      ) : loading ? <p className="muted">{t("workload.overview.podsLoading")}</p> : <DegradedSection reason={section?.reason} />}
    </Panel>
  );
}

function DegradedSection({ reason }: { reason?: string }) {
  const { t } = useT("workload");
  if (reason === "result_too_large") return <p role="alert">{t("workload.overview.degradedTooLarge")}</p>;
  if (reason === "duplicate_uid") return <p role="alert">{t("workload.overview.degradedDuplicate")}</p>;
  if (reason === "projection_syncing") return <p role="status">{t("workload.overview.degradedSyncing")}</p>;
  return <p role="alert">{t("workload.overview.degradedUnavailable")}</p>;
}

const POD_COLUMNS = ["minmax(240px,2fr)", "120px", "90px", "100px", "150px", "minmax(180px,1fr)"];

function PodTable({ rows, search }: { rows: WorkloadsOverviewPodRow[]; search: string }) {
  const { t } = useT("workload");
  return (
    <VirtualTable
      items={rows}
      rowHeight={56}
      height={620}
      columns={POD_COLUMNS}
      contentWidth={980}
      getKey={(row) => row.uid}
      empty={null}
      header={<tr><th>{t("workload.overview.columnPod")}</th><th>{t("workload.overview.columnStatus")}</th><th className="ds-num">{t("workload.overview.columnReady")}</th><th className="ds-num">{t("workload.overview.columnRestarts")}</th><th>{t("workload.overview.columnNodeIP")}</th><th>{t("workload.overview.columnOwner")}</th></tr>}
      renderRow={(row) => (
        <>
          <td className="ds-ident">
            <Link to={refPath({ namespace: row.namespace, podName: row.name, podUid: row.uid }, search)}>{row.name}</Link>
            <div className="muted">{row.namespace} · {row.qosClass}</div>
          </td>
          <td><StatusBadge severity={row.severity} label={row.phase} small /></td>
          <td className="ds-num">{row.ready}</td>
          <td className="ds-num">{num(row.restarts)}</td>
          <td>{boundedText(row.node, t)}<div className="muted">{boundedText(row.primaryIP, t)}</div></td>
          <td>{ownerCell(row, search, t)}</td>
        </>
      )}
    />
  );
}

function ownerCell(row: WorkloadsOverviewPodRow, search: string, t: Translate) {
  if (row.resolvedWorkloadKind && row.resolvedWorkloadName && row.resolvedWorkloadUid) {
    return (
      <Link to={refPath({
        namespace: row.namespace,
        workloadKind: row.resolvedWorkloadKind,
        workloadName: row.resolvedWorkloadName,
        workloadUid: row.resolvedWorkloadUid,
      }, search)}>
        {row.ownerChain.map((owner) => owner.name).join(" → ")}
      </Link>
    );
  }
  if (!row.ownerChain.length) return <span className="muted">{t("workload.overview.standalone")}</span>;
  return <span>{row.ownerChain.map((owner) => owner.name).join(" → ")}{row.ownerChainComplete ? "" : ` · ${t("workload.overview.chainUnavailable")}`}</span>;
}

function boundedText(value: BoundedText, t: Translate): string {
  if (value.state === "ok") return value.value ?? t("workload.absent");
  if (value.state === "withheld_too_large") return t("workload.withheld");
  return t("workload.absent");
}

function issueLabel(reason: IssueReason, t: Translate): string {
  switch (reason) {
    case "CrashLoopBackOff": return t("workload.overview.issueCrashLoopBackOff");
    case "ImagePullBackOff": return t("workload.overview.issueImagePullBackOff");
    case "Pending": return t("workload.overview.issuePending");
    case "ReplicaMismatch": return t("workload.overview.issueReplicaMismatch");
    case "RolloutStalled": return t("workload.overview.issueRolloutStalled");
    case "Restarting": return t("workload.overview.issueRestarting");
    case "OOMKilled": return t("workload.overview.issueOOMKilled");
    case "ProbeFailed": return t("workload.overview.issueProbeFailed");
  }
}
